import {
  ApprovalMasterLiteModel,
  useGetApprovalMasters,
} from 'api-hooks/approval-master';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ApprovalMasterTable from './components/table';

export default function ApprovalMasterList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);

  const _columns = React.useMemo<IColumn<ApprovalMasterLiteModel>[]>(
    () => [
      {
        header: t('common:model'),
        accessorKey: 'model',
        minSize: 250,
        sortName: 'model',
      },

      {
        header: t('common:role'),
        accessorKey: 'approvalDetails',
        minSize: 250,
        cell: ({ getValue }) =>
          (getValue() as any).map((item) => item.role.name).join(', '),
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetApprovalMasters,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const permission = useGetCrudPermission('approvals');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:approval')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.ApprovalMastersCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <ApprovalMasterTable
          approval={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          sortColumns={columns || []}
          meta={data?.meta}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
